import type { Recurrence } from './Recurrence'
import type { Address, GeoPoint } from 'types/entities/Address'
import type { IsoDateString, PricedServiceType, TagResponse, WeekDay, WorkOrderException, WorkOrderStatus } from 'types/entities/shared'
import type { CseId, MasterStopId, RouteId, RouteSlotId, StopId, WorkOrderId } from 'types/entities/shared/ids'

export interface RouteAssignment {
  routeId: RouteId
  sequence: number
  dayOfWeek: WeekDay | null
  serviceDate: IsoDateString | null
}

interface StopBase {
  accountId: string
  accountName: string
  configuredServiceId: string
  configuredServiceEventId: CseId | null
  serviceName: string
  pricedServiceType: PricedServiceType
  serviceActionType: string | null
  eventActionType: string | null
  addressName: string | null
  locationName: string | null
  address: Address | null
  geo: GeoPoint | null
  recurrence: Recurrence | null
  routeAssignment: RouteAssignment
  tags: TagResponse | null
}

/**
 * Recurrence-level stop on a master route. No workOrderId, no per-day status —
 * those don't exist until a daily instance is materialized.
 * `configuredServiceEventId` is non-null on master stops by definition (master
 * rows are CSE-keyed).
 */
export interface MasterStop extends StopBase {
  kind: 'master'
  id: MasterStopId
  configuredServiceEventId: CseId
  nearestRoad: GeoPoint | null
  routeSlotId?: RouteSlotId | null
  // Service validity — BE-computed on the master row. Consumed by the
  // promote-daily-to-master engine to decide whether a shared cse can carry
  // over (a service that ended or an account that deactivated can't). Optional
  // so existing fixtures/constructors that predate these stay valid.
  endDate?: IsoDateString | null
  serviceEnded?: boolean | null
  serviceDeactivated?: boolean | null
}

/**
 * A master row keyed on `routeSlotId` alone — a disposal stop today — with no configured service
 * event and no account or service columns. The `null` keeps it out of the CSE-keyed master steps.
 */
export interface RoutableMasterStop
  extends Omit<
    MasterStop,
    'kind' | 'configuredServiceEventId' | 'accountId' | 'accountName' | 'configuredServiceId' | 'serviceName' | 'pricedServiceType'
  > {
  kind: 'master-routable'
  configuredServiceEventId: null
  accountId: string | null
  accountName: string | null
  configuredServiceId: string | null
  serviceName: string | null
  pricedServiceType: PricedServiceType | null
}

/**
 * Instance-level stop on a daily route for a specific service date.
 * `workOrderStatus` is required — daily stops always carry one.
 */
export interface DailyStop extends StopBase {
  kind: 'daily'
  workOrderId: WorkOrderId
  stopId: StopId
  configuredServiceEventId: CseId
  workOrderNumber: string
  workOrderStatus: WorkOrderStatus
  servicedAt: string | null
  exceptions: ReadonlyArray<WorkOrderException>
  inMaster: boolean | null
  suspensionHold: boolean | null
  routeSlotId?: RouteSlotId | null
  // Service validity mirrored onto the daily row (WOLV-4772) — the promote
  // engine reads these so a daily-only stop still classifies as ended /
  // deactivated. Optional so fixtures/constructors predating them stay valid.
  endDate?: IsoDateString | null
  serviceEnded?: boolean | null
  serviceDeactivated?: boolean | null
  serviceStatus?: string | null
  eventDeleted?: boolean | null
}

/**
 * A daily-only stop with no master row. The pipelines keep it off master, not this type: CSE-keyed
 * legs gate on `configuredServiceEventId`, slot-keyed ones on `kind` — the inherited slot survives.
 */
export interface OnRequestStop extends Omit<DailyStop, 'kind' | 'configuredServiceEventId'> {
  kind: 'on-request'
  configuredServiceEventId: null
}

export type Stop = MasterStop | RoutableMasterStop | DailyStop | OnRequestStop

export const isMasterStop = (stop: Stop): stop is MasterStop => stop.kind === 'master'
export const isDailyStop = (stop: Stop): stop is DailyStop => stop.kind === 'daily'
export const isOnRequestStop = (stop: Stop): stop is OnRequestStop => stop.kind === 'on-request'
export const isRoutableMasterStop = (stop: Stop): stop is RoutableMasterStop => stop.kind === 'master-routable'

/**
 * Transient flags the master sequence editor and the dialogs that hand off
 * to it write onto stops. None of these come from the BE — they're computed
 * in-flight and consumed by RenderCell for styling and by the prepopulation
 * transforms for branching.
 */
export interface RouteStopAnnotations {
  isAssigned?: boolean
  isUnassigned?: boolean
  isChanged?: boolean
  isPending?: boolean
  toBeInserted?: boolean
  // Smart-Neighbors fan-out landed this stop at the route head because none of
  // its master neighbours are scheduled on this daily — drives the fallback
  // highlight + chip reason (CS-2918).
  fallbackLanded?: boolean
  // Origin of a stop pulled in from another route (bulk "Move between routes →
  // Edit master sequence"). Drives the cross-route chip (CS-2918).
  sourceRouteId?: string | null
  sourceRouteName?: string | null
}

export type AnnotatedMasterStop = MasterStop & RouteStopAnnotations
export type AnnotatedDailyStop = DailyStop & RouteStopAnnotations
export type AnnotatedStop = AnnotatedMasterStop | AnnotatedDailyStop
